// The turn row: whose move it is, what the game is waiting on, and the things
// a player can do that are not a move (PLAN-UI §28.3, U3).
//
// The model behind `TurnRowZone`, which derived all of this inline until day 6
// of the client split. What only the shell knows — the socket, the difficulty
// being played, the watch transport — stays with the shell.

import { stagesCleared } from "src/engine/ai/waves";
import { spawnIn, waveIn } from "src/engine/rules/campaign/pending";
import { fodderMovesFor, movesForSide } from "src/engine/rules/campaign/titan";
import { movesOpen } from "src/engine/rules/clock";
import type { GameState } from "src/engine/rules/commands";
import { rulesetOf } from "src/engine/rules/ruleset";
import type { Color } from "src/shared/board";
import { REFUSAL } from "./refusals";
import { type TurnLabel, turnLabel } from "./turnLabel";

/** The status, as the chip says it. Empty while simply playing. */
export const STATUS_LABEL: Record<string, string> = {
  playing: "",
  check: "Check",
  checkmate: "Checkmate",
  stalemate: "Stalemate",
  "draw-fifty-move": "Draw — fifty-move rule",
  "draw-repetition": "Draw — threefold repetition",
  "draw-material": "Draw — insufficient material"
};

export interface TurnRowModel {
  terminal: boolean;
  status: string;
  turn: TurnLabel;
  /** Power Moves (§33 T1): Monarch and Power pieces spend these. */
  moves: { left: number; total: number };
  /** Fodder Moves (§33 T1): Power Moves × Army Logistics, for fodder only. */
  fodder: { left: number; total: number };
  /**
   * The front this game is on, from one — a scenario clears fronts and takes
   * waves exactly as a run does, so it counts on every board. **The shell
   * decides whether to show it**: versus has no fronts to count.
   */
  stage: number;
  waveOwed: number | null;
  /** Its own reading, never folded into `waveOwed`: the two chips say different sentences. */
  clockOwed: number | null;
  /** The run's camps land on a clock, so the chip counts one down. */
  spawnClock: boolean;
  /** Why End turn is off, or null. */
  endTurnRefusal: string | null;
  /**
   * Your moves are spent and the clock is still yours (§31 T2): End turn is
   * the one thing the game is waiting on, so the row says so.
   */
  endTurnReady: boolean;
}

export interface TurnRowInput {
  state: GameState;
  localSide: Color;
  sideToMove: string;
  viewing: boolean;
}

export function turnRowModel({ state, localSide, sideToMove, viewing }: TurnRowInput): TurnRowModel {
  const terminal = state.status !== "playing" && state.status !== "check";
  const left = state.position.movesLeft;
  // `movesForSide` is the real allowance, and Reactor Boosters raises
  // `movesLeft` above it for one turn — so the ceiling is whichever is larger.
  const total = Math.max(movesForSide(state.position, state.industry, state.position.turn), left);
  const fodderLeft = state.position.fodderLeft;
  const fodderTotal = Math.max(fodderMovesFor(state.position, state.industry, state.position.turn), fodderLeft);
  const endTurnRefusal = viewing
    ? REFUSAL["viewing-history"]
    : terminal
      ? "The game is over."
      : state.position.turn !== localSide
        ? `It is ${sideToMove}'s turn.`
        : state.status === "check"
          ? "You cannot end your turn while in check."
          : null;
  return {
    terminal,
    status: STATUS_LABEL[state.status] ?? "",
    turn: turnLabel(sideToMove, left, total),
    moves: { left, total },
    fodder: { left: fodderLeft, total: fodderTotal },
    stage: stagesCleared(state) + 1,
    waveOwed: waveIn(state),
    clockOwed: spawnIn(state),
    spawnClock: rulesetOf(state).spawn === "clock",
    // The view answers first (§29 U4): the turn this would end is not the one
    // being read. Then the reducer's own two: a finished game, and check.
    endTurnRefusal,
    // Nothing left that may move (§33 T1): both pools spent, or every piece
    // that could spend one has moved.
    endTurnReady: endTurnRefusal === null && !movesOpen(state)
  };
}
